import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import Table from '../../components/common/Table';
import Pagination from '../../components/common/Pagination';
import { LuPlus, LuPencil, LuTrash2, LuSearch, LuCar, LuToggleLeft, LuToggleRight } from 'react-icons/lu';
import { useAuth } from '../../context/AuthContext';

const EMPTY = { name: '', phone: '', licenseNumber: '', vehicle: { type: 'bus', number: '', model: '', capacity: 40 } };

export default function Drivers() {
  const { isAdmin } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dutyFilter, setDutyFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const PAGE_SIZE = 10;
  const [modal, setModal] = useState({ open: false, mode: 'add', data: null });
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (search)     params.set('search',     search);
      if (dutyFilter) params.set('dutyStatus', dutyFilter);
      const { data } = await api.get(`/drivers?${params}`);
      setDrivers(data.data || []);
      setPagination(data.pagination || null);
    } catch { toast.error('Failed to load drivers'); }
    finally { setLoading(false); }
  }, [page, search, dutyFilter]);

  useEffect(() => { load(); }, [load]);

  const setField   = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setVehicle = k => e => setForm(f => ({ ...f, vehicle: { ...f.vehicle, [k]: e.target.value } }));

  const openAdd  = () => { setForm(EMPTY); setModal({ open: true, mode: 'add', data: null }); };
  const openEdit = r => {
    setForm({ name: r.name, phone: r.phone, licenseNumber: r.licenseNumber || '', vehicle: r.vehicle || EMPTY.vehicle });
    setModal({ open: true, mode: 'edit', data: r });
  };
  const close = () => setModal({ open: false, mode: 'add', data: null });

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (modal.mode === 'add') await api.post('/drivers', form);
      else await api.put(`/drivers/${modal.data._id}`, form);
      toast.success(modal.mode === 'add' ? 'Driver added!' : 'Updated!');
      close(); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error saving'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this driver?')) return;
    try { await api.delete(`/drivers/${id}`); toast.success('Removed'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const toggleDuty = async (id) => {
    try {
      const res = await api.patch(`/drivers/${id}/duty`);
      toast.success(res.data.message);
      load();
    } catch { toast.error('Failed to update duty status'); }
  };

  const prevDepsRef = useRef(`${search}|${dutyFilter}`);
  useEffect(() => {
    const key = `${search}|${dutyFilter}`;
    if (prevDepsRef.current !== key) { prevDepsRef.current = key; setPage(1); }
  });

  const statusClass = { active: 'bg-green-50 text-green-700', inactive: 'bg-red-50 text-red-600', on_trip: 'bg-hajj-gold/15 text-amber-700' };

  const columns = [
    { key: 'name', label: 'Driver', render: r => (
      <div className="flex items-center gap-3">
        {r.photo
          ? <img src={r.photo} alt={r.name} className="w-9 h-9 rounded-full object-cover flex-shrink-0 border border-gray-100" />
          : <div className="w-9 h-9 rounded-full bg-hajj-teal/15 flex items-center justify-center flex-shrink-0 text-sm font-bold text-teal-700">{r.name.charAt(0).toUpperCase()}</div>
        }
        <span className="font-semibold text-hajj-dark">{r.name}</span>
      </div>
    )},
    { key: 'phone', label: 'Phone' },
    { key: 'licenseNumber', label: 'License', render: r => r.licenseNumber || <span className="text-gray-300">—</span> },
    { key: 'vehicle', label: 'Vehicle', render: r => r.vehicle ? (
      <div>
        <div className="font-medium text-hajj-dark">{r.vehicle.number || '—'}</div>
        <div className="text-xs text-gray-400">{r.vehicle.type} · {r.vehicle.capacity} seats</div>
      </div>
    ) : '—'},
    { key: 'status', label: 'Status', render: r => <span className={`badge ${statusClass[r.status]}`}>{r.status.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</span> },
    { key: 'dutyStatus', label: 'Duty', render: r => (
      <button
        onClick={() => isAdmin && toggleDuty(r._id)}
        title={isAdmin ? 'Toggle duty status' : undefined}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
          r.dutyStatus === 'on_duty'
            ? 'bg-green-100 text-green-700 hover:bg-green-200'
            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
        } ${!isAdmin ? 'cursor-default' : 'cursor-pointer'}`}
      >
        {r.dutyStatus === 'on_duty'
          ? <><LuToggleRight size={14} /> On Duty</>
          : <><LuToggleLeft size={14} /> Off Duty</>}
      </button>
    )},
    ...(isAdmin ? [{ key: 'actions', label: 'Action', width: 80, render: r => (
      <div className="flex gap-1.5">
        <button onClick={() => openEdit(r)} className="w-8 h-8 rounded-lg bg-hajj-teal/10 text-teal-700 flex items-center justify-center hover:bg-hajj-teal/20 transition-colors"><LuPencil size={14} /></button>
        <button onClick={() => handleDelete(r._id)} className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors"><LuTrash2 size={14} /></button>
      </div>
    )}] : []),
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 text-hajj-dark">
          <LuCar className="text-xl sm:text-2xl" />
          <h1 className="text-xl sm:text-2xl font-extrabold">Drivers</h1>
        </div>
        {isAdmin && <button className="btn-primary text-sm" onClick={openAdd}><LuPlus size={15} /> Add Driver</button>}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
        <div className="relative w-full sm:max-w-xs">
          <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input placeholder="Search name, phone, vehicle..." value={search} onChange={e => setSearch(e.target.value)} className="input-field pl-9 w-full" />
        </div>
        <select value={dutyFilter} onChange={e => setDutyFilter(e.target.value)} className="input-field w-full sm:w-40">
          <option value="">All Duty</option>
          <option value="on_duty">On Duty</option>
          <option value="off_duty">Off Duty</option>
        </select>
        <span className="text-sm text-gray-400 sm:ml-auto">{pagination?.total ?? drivers.length} drivers</span>
      </div>

      <div className="card">
        <Table columns={columns} data={drivers} loading={loading} emptyMessage="No drivers found" />
        <Pagination page={page} total={pagination?.total || 0} pageSize={PAGE_SIZE} onPage={setPage} />
      </div>

      <Modal isOpen={modal.open} onClose={close} title={modal.mode === 'add' ? 'Add Driver' : 'Edit Driver'} size="lg">
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Driver Details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Full Name *</label>
                <input value={form.name} onChange={setField('name')} required className="input-field" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Phone *</label>
                <div className="flex w-full border border-gray-200 rounded-xl overflow-hidden focus-within:border-hajj-teal focus-within:ring-2 focus-within:ring-hajj-teal/10 transition-all duration-200">
                  <span className="flex items-center px-3 bg-gray-50 border-r border-gray-200 text-sm text-gray-600 font-medium select-none whitespace-nowrap">+966</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={form.phone.replace(/^\+966/, '')}
                    onChange={e => { const d = e.target.value.replace(/\D/g, ''); setForm(f => ({ ...f, phone: '+966' + d })); }}
                    required
                    maxLength={9}
                    placeholder="5XXXXXXXX"
                    className="flex-1 px-3 py-2.5 text-sm text-gray-800 bg-white outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">License Number</label>
                <input value={form.licenseNumber} onChange={setField('licenseNumber')} className="input-field" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Vehicle Details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Vehicle Type</label>
                <select value={form.vehicle?.type} onChange={setVehicle('type')} className="input-field">
                  <option value="bus">Bus</option><option value="minibus">Minibus</option>
                  <option value="van">Van</option><option value="car">Car</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Vehicle Number</label>
                <input value={form.vehicle?.number} onChange={setVehicle('number')} className="input-field" placeholder="e.g. ABC 1234" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Model</label>
                <input value={form.vehicle?.model} onChange={setVehicle('model')} className="input-field" placeholder="e.g. Toyota Coaster" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Capacity (seats)</label>
                <input type="number" value={form.vehicle?.capacity} onChange={setVehicle('capacity')} className="input-field" min={1} />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" className="btn-secondary" onClick={close}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving && <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              {modal.mode === 'add' ? 'Add Driver' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
